import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

export type OtpInputProps = {
    value: string;
    onChange: (value: string) => void;
    numInputs?: number;
    shouldAutoFocus?: boolean;
    hasErrored?: boolean;
    isInputNum?: boolean;
    className?: string;
};

/**
 * Six-cell OTP UI compatible with existing `.otp-input > input` styles (replaces react-otp-input).
 */
export default function OtpInput({
    value,
    onChange,
    numInputs = 6,
    shouldAutoFocus = true,
    className = '',
}: OtpInputProps) {
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

    const digits = (value || '').replace(/\D/g, '').slice(0, numInputs);
    const chars: string[] = [];
    for (let i = 0; i < numInputs; i += 1) {
        chars.push(digits[i] ?? '');
    }

    useEffect(() => {
        if (shouldAutoFocus) {
            inputsRef.current[0]?.focus();
        }
    }, [shouldAutoFocus]);

    const focusAt = (i: number) => {
        const idx = Math.min(Math.max(i, 0), numInputs - 1);
        inputsRef.current[idx]?.focus();
    };

    const commit = (nextDigits: string) => {
        onChange(nextDigits.replace(/\D/g, '').slice(0, numInputs));
    };

    const handleChange = (index: number, raw: string) => {
        const digit = raw.replace(/\D/g, '').slice(-1);
        if (digit) {
            const arr = chars.slice();
            arr[index] = digit;
            commit(arr.join(''));
            if (index < numInputs - 1) {
                focusAt(index + 1);
            }
        } else {
            const arr = chars.slice();
            arr[index] = '';
            commit(arr.join(''));
        }
    };

    const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !chars[index] && index > 0) {
            e.preventDefault();
            focusAt(index - 1);
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, numInputs);
        commit(pasted);
        focusAt(Math.min(pasted.length, numInputs - 1));
    };

    return (
        <div className={['otp-input', className].filter(Boolean).join(' ')}>
            {Array.from({ length: numInputs }, (_, i) => (
                <input
                    key={i}
                    ref={(el) => {
                        inputsRef.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    value={chars[i]}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={i === 0 ? handlePaste : undefined}
                />
            ))}
        </div>
    );
}
